import { useState, useEffect } from "react";
import API from "@/lib/api";
import { formatCurrency, formatPercent } from "@/lib/formatters";
import MetricCard from "@/components/shared/MetricCard";
import DataTable from "@/components/shared/DataTable";
import USMapGrid from "@/components/shared/USMapGrid";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Globe, DollarSign, TrendingUp, Building2, Plus, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import AddEditDialog from "@/components/shared/AddEditDialog";
import UploadDialog from "@/components/shared/UploadDialog";

export default function GeographicAnalytics() {
  const [geo, setGeo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    API.get("/geography")
      .then((res) => setGeo(res.data.geography || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const totalVal = geo.reduce((a, g) => a + g.value, 0);
  const avgIrr = geo.length ? geo.reduce((a, g) => a + g.irr, 0) / geo.length : 0;
  const totalProps = geo.reduce((a, g) => a + g.num_properties, 0);

  const columns = [
    { key: "state", label: "State", sortable: false, render: (v) => <span className="font-medium text-foreground">{v}</span> },
    { key: "state_code", label: "Code" },
    { key: "value", label: "Value", align: "right", render: (v) => formatCurrency(v) },
    { key: "irr", label: "IRR", align: "right", render: (v) => <span className="text-emerald-500">{v}%</span> },
    { key: "num_properties", label: "Properties", align: "right" },
    { key: "occupancy", label: "Occupancy", align: "right", render: (v) => `${v}%` },
    { key: "cap_rate", label: "Cap Rate", align: "right", render: (v) => `${v}%` },
    { key: "noi", label: "NOI", align: "right", render: (v) => formatCurrency(v) },
  ];

  if (loading) return <div className="p-6"><Skeleton className="h-96" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="geographic-analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Geographic Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Portfolio exposure by state and region</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-geo-btn" className="gap-1"><Plus size={14} />Add</Button>
          <Button size="sm" variant="secondary" onClick={() => setShowUpload(true)} data-testid="upload-geo-btn" className="gap-1"><Upload size={14} />Upload</Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <MetricCard label="Total Exposure" value={formatCurrency(totalVal)} icon={DollarSign} />
        <MetricCard label="States Active" value={geo.length} icon={Globe} />
        <MetricCard label="Avg State IRR" value={formatPercent(avgIrr)} icon={TrendingUp} />
        <MetricCard label="Total Properties" value={totalProps} icon={Building2} />
      </div>

      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-8 border border-border bg-card">
          <CardHeader className="border-b border-border p-3">
            <CardTitle className="text-sm font-semibold">US Portfolio Heatmap</CardTitle>
          </CardHeader>
          <CardContent className="p-6" data-testid="geo-map">
            <USMapGrid data={geo} onStateClick={setSelected} />
          </CardContent>
        </Card>

        <Card className="col-span-4 border border-border bg-card">
          <CardHeader className="border-b border-border p-3">
            <CardTitle className="text-sm font-semibold">
              {selected ? selected.state : "State Detail"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4" data-testid="state-detail">
            {selected ? (
              <div className="space-y-3">
                {[
                  ["Value", formatCurrency(selected.value)],
                  ["IRR", `${selected.irr}%`],
                  ["Properties", selected.num_properties],
                  ["Occupancy", `${selected.occupancy}%`],
                  ["Cap Rate", `${selected.cap_rate}%`],
                  ["NOI", formatCurrency(selected.noi)],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                    <span className="text-sm text-muted-foreground">{label}</span>
                    <span className="text-sm font-data font-medium">{value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Click a state on the map to view details</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border border-border bg-card">
        <CardHeader className="border-b border-border p-3">
          <CardTitle className="text-sm font-semibold">State Performance</CardTitle>
        </CardHeader>
        <CardContent className="p-0" data-testid="geography-table">
          <DataTable columns={columns} data={geo} />
        </CardContent>
      </Card>
      <AddEditDialog open={showAdd} entityType="geography" onClose={() => setShowAdd(false)} onSave={refresh} />
      <UploadDialog open={showUpload} entityType="geography" onClose={() => setShowUpload(false)} onSuccess={refresh} />
    </div>
  );
}
